import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Flame, Pencil, CheckCircle2, RefreshCw, Inbox as InboxIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTenant } from '@/contexts/TenantContext';
import { listPendingWildfires, submitWildfire } from '@/api/wildfires';

interface PendingFire {
  id: number;
  fire_reference?: string | null;
  suburb?: string | null;
  address?: string | null;
  start_time?: string | null;
}

function formatDate(s?: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Admin Inbox — the approval queue for pending content. For now it surfaces
 * DRAFT wildfire reports (filed by users who can't self-approve); an approver
 * can submit them from here. Image/video moderation lives on the Content
 * Moderation page, linked below.
 */
export default function AdminInbox({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const { tenantId } = useTenant();
  const [fires, setFires] = useState<PendingFire[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await listPendingWildfires(
        tenantId ? { tenant_id: tenantId } : {},
      )) as unknown as { wildfires?: PendingFire[] };
      const items = data.wildfires || [];
      setFires(items);
      onCountChange?.(items.length);
    } catch (err) {
      console.error('Failed to load pending drafts:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (id: number) => {
    setSubmittingId(id);
    try {
      await submitWildfire(id);
      toast.success('Report submitted');
      await load();
    } catch (err) {
      console.error('Submit failed:', err);
      toast.error('Failed to submit report');
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <InboxIcon className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Pending draft reports</h2>
          <Badge variant="secondary">{fires.length}</Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : fires.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-10 text-sm text-muted-foreground">
          <CheckCircle2 className="h-8 w-8 opacity-30" />
          No reports awaiting approval.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {fires.map((w) => (
            <li key={w.id} className="flex items-center gap-3 px-4 py-3">
              <Flame className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/wildfires/${w.id}`}
                    className="truncate font-mono text-sm font-semibold hover:underline"
                  >
                    {w.fire_reference || `#${w.id}`}
                  </Link>
                  <Badge variant="outline" className="gap-1 border-amber-400 text-amber-700">
                    <Pencil className="h-3 w-3" /> Draft
                  </Badge>
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {w.suburb || 'Unknown suburb'}
                  {w.address ? ` · ${w.address}` : ''} · {formatDate(w.start_time)}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => handleSubmit(w.id)}
                disabled={submittingId === w.id}
                className="gap-1.5"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {submittingId === w.id ? 'Submitting…' : 'Submit'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="text-xs text-muted-foreground">
        Looking for photo/video approvals?{' '}
        <Link to="/images/ContentModeration" className="text-primary hover:underline">
          Open Content Moderation
        </Link>
        .
      </div>
    </div>
  );
}
